/**
 * Cliente del catálogo de plantillas.
 *
 * Backend: `app/api/v1/templates.py`. Doc 06.
 *
 * Cobertura B.5.b.1 (read-only):
 *   - listTemplates(filters?)
 *   - getTemplate(id)
 *   - getCurrentVersion(id)         — incluye `definition` JSON completo
 *   - listVersions(id)
 *
 * Las acciones de escritura (clone/edit draft/publish/archive) llegan en
 * B.5.b.2 con el editor JSON.
 */

import { apiFetch } from './client'
import type { SupportedLocale } from '../i18n/config'

export type TemplateType =
  | 'lifecycle'
  | 'phase'
  | 'subphase'
  | 'artifact'
  | 'methodology'
  | 'approval_workflow'
  | 'role'

export type TemplateStatus = 'draft' | 'published' | 'deprecated' | 'archived'

export interface Template {
  id: string
  /** NULL para system-default; UUID para custom */
  tenant_id: string | null
  template_type: TemplateType
  template_key: string
  name: string
  description: string | null
  is_system_default: boolean
  status: TemplateStatus
  current_version_id: string | null
  /** Version string del current_version_id (ej. "3.4.0"). Lo resuelve
   * el backend con un JOIN a `template_versions`. `null` cuando el
   * template no tiene versión activa todavía. Lo consume la card del
   * catálogo para mostrar la versión real (no un placeholder). */
  current_version: string | null
  parent_template_id: string | null
  /** Metodología a la que pertenece (OT-0053). `null` = genérica: la usa
   * cualquier metodología ("Base" en la UI si es del producto). Un ciclo
   * de vida siempre tiene una. */
  methodology_id: string | null
  created_at: string
  updated_at: string
  /** Scopes a los que aplica el template (extracto de
   * `current_version.definition.applicable_scopes`). Para roles, valores
   * son `['internal']`, `['client_stakeholder']`, o ambos. `null` para
   * templates que no declaran el campo. Approval Workflow Fase 0. */
  applicable_scopes?: string[] | null
  // OT-0008 D7/D8 — `is_project_lead` e `is_observer` salieron del
  // contrato. Otorgaban permisos desde el catálogo de roles, y el rol
  // funcional no otorga permisos: el liderazgo es de una persona
  // (`projects.lead_member_id`) y lo que alguien puede hacer sale de su
  // tier de licencia.
}

export interface TemplateVersion {
  id: string
  template_id: string
  version: string
  change_log: string | null
  published_at: string | null
  created_at: string
}

/**
 * Versión con `definition` completa — el JSON serializado de la plantilla.
 * Por contrato Doc 06 el JSON contiene tanto la metadata estructural
 * (`section_id`, `type`, `order`, etc.) como el bloque `i18n.{es,en}`
 * (Doc 19 §3.3). El frontend renderiza un lado u otro según el
 * idioma activo (o el del proyecto cuando llegue al editor del proyecto).
 */
export interface TemplateVersionWithDefinition extends TemplateVersion {
  definition: Record<string, unknown>
}

export interface ListTemplatesFilters {
  /** Filtra por tipo (phase, subphase, artifact, ...). */
  template_type?: TemplateType | undefined
  /** Filtra por status (draft, published, deprecated, archived). */
  template_status?: TemplateStatus | undefined
  /** ILIKE substring sobre `name` y `template_key` (B.6.d.0 backend). */
  search?: string | undefined
  /** Solo las que puede usar un proyecto con ese ciclo: los miembros de su
   * metodología (OT-0006). */
  lifecycle_id?: string | undefined
}

export async function listTemplates(
  accessToken: string,
  filters?: ListTemplatesFilters,
): Promise<Template[]> {
  const params = new URLSearchParams()
  if (filters?.template_type) params.set('template_type', filters.template_type)
  if (filters?.template_status)
    params.set('template_status', filters.template_status)
  if (filters?.search && filters.search.trim()) {
    params.set('search', filters.search.trim())
  }
  if (filters?.lifecycle_id) params.set('lifecycle_id', filters.lifecycle_id)
  const qs = params.toString()
  return apiFetch<Template[]>(
    `/api/v1/templates${qs ? `?${qs}` : ''}`,
    { accessToken },
  )
}

export async function getTemplate(
  accessToken: string,
  templateId: string,
): Promise<Template> {
  return apiFetch<Template>(`/api/v1/templates/${templateId}`, { accessToken })
}

export async function getCurrentVersion(
  accessToken: string,
  templateId: string,
): Promise<TemplateVersionWithDefinition> {
  return apiFetch<TemplateVersionWithDefinition>(
    `/api/v1/templates/${templateId}/current`,
    { accessToken },
  )
}

export async function listVersions(
  accessToken: string,
  templateId: string,
): Promise<TemplateVersion[]> {
  return apiFetch<TemplateVersion[]>(
    `/api/v1/templates/${templateId}/versions`,
    { accessToken },
  )
}

// ─── Calidad (OT-0006) ──────────────────────────────────────────────────────
/** `blocking` impide publicar; `error` y `warning` no. */
export type QualitySeverity = 'blocking' | 'error' | 'warning'

/** Hallazgo del reporte de calidad (`app/services/template_quality.py`).
 * El texto lo pone la UI: `templates:quality.findings.<code>` con `params`. */
export interface QualityFinding {
  code: string
  severity: QualitySeverity
  template_key: string | null
  template_type: TemplateType | null
  /** Ruta dentro de la definición, p.ej. `sections[2].children[0]`. */
  location: string | null
  params: Record<string, string | number>
}

export interface QualityReport {
  is_publishable: boolean
  findings: QualityFinding[]
}

export function versionQualityQueryKey(templateId: string, versionId: string) {
  return ['templates', templateId, 'versions', versionId, 'quality'] as const
}

export async function getVersionQuality(
  accessToken: string,
  templateId: string,
  versionId: string,
): Promise<QualityReport> {
  return apiFetch<QualityReport>(
    `/api/v1/templates/${templateId}/versions/${versionId}/quality`,
    { accessToken },
  )
}

// ─── Mutations (B.5.b.2) ────────────────────────────────────────────────────

export interface CloneTemplateBody {
  name?: string
  description?: string
  /** Metodología del tenant a la que va el clon. Obligatoria al clonar un
   * ciclo de vida (422 `METHODOLOGY_REQUIRED_FOR_LIFECYCLE`). */
  methodology_id?: string
}

export async function cloneTemplate(
  accessToken: string,
  templateId: string,
  body: CloneTemplateBody = {},
): Promise<Template> {
  return apiFetch<Template>(
    `/api/v1/templates/${templateId}/clone`,
    { accessToken, method: 'POST', body },
  )
}

export interface UpdateTemplateBody {
  name?: string
  description?: string
}

export async function updateTemplate(
  accessToken: string,
  templateId: string,
  body: UpdateTemplateBody,
): Promise<Template> {
  return apiFetch<Template>(`/api/v1/templates/${templateId}`, {
    accessToken,
    method: 'PUT',
    body,
  })
}

export interface CreateVersionBody {
  /** Semver: '1.0.0', '1.2.0-beta', etc. */
  version: string
  definition: Record<string, unknown>
  change_log?: string
}

export async function createVersion(
  accessToken: string,
  templateId: string,
  body: CreateVersionBody,
): Promise<TemplateVersion> {
  return apiFetch<TemplateVersion>(
    `/api/v1/templates/${templateId}/versions`,
    { accessToken, method: 'POST', body },
  )
}

export interface UpdateVersionDefinitionBody {
  definition: Record<string, unknown>
  change_log?: string
}

export async function updateVersionDefinition(
  accessToken: string,
  templateId: string,
  versionId: string,
  body: UpdateVersionDefinitionBody,
): Promise<TemplateVersionWithDefinition> {
  return apiFetch<TemplateVersionWithDefinition>(
    `/api/v1/templates/${templateId}/versions/${versionId}`,
    { accessToken, method: 'PATCH', body },
  )
}

export async function publishVersion(
  accessToken: string,
  templateId: string,
  versionId: string,
): Promise<TemplateVersion> {
  return apiFetch<TemplateVersion>(
    `/api/v1/templates/${templateId}/versions/${versionId}/publish`,
    { accessToken, method: 'POST' },
  )
}

export async function archiveTemplate(
  accessToken: string,
  templateId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/templates/${templateId}`, {
    accessToken,
    method: 'DELETE',
  })
}

/** Restaura una plantilla archivada (B.6.d.8.g). El status pasa a
 * `published` si tiene versión publicada, `draft` si solo tiene drafts. */
export async function restoreTemplate(
  accessToken: string,
  templateId: string,
): Promise<Template> {
  return apiFetch<Template>(`/api/v1/templates/${templateId}/restore`, {
    accessToken,
    method: 'POST',
  })
}

/** Descarta un draft y revierte `current_version_id` a la última
 * versión publicada (B.6.d.8.g). Falla si no hay publicada anterior. */
export async function discardDraftVersion(
  accessToken: string,
  templateId: string,
  versionId: string,
): Promise<Template> {
  return apiFetch<Template>(
    `/api/v1/templates/${templateId}/versions/${versionId}/discard`,
    { accessToken, method: 'POST' },
  )
}

// ─── Create from scratch (B.6.d.0) ──────────────────────────────────────────

export interface CreateTemplateBody {
  template_type: TemplateType
  /** Identificador estable (`tpl-mi-plantilla`). Único por tenant + system. */
  template_key: string
  name: string
  description?: string | undefined
  /** Shape canónico Doc 06 §1.1 (artifact) o §5 (lifecycle). El backend
   * valida con `TemplateDefinitionSchema` y rechaza con 400
   * `TEMPLATE_INVALID_DEFINITION` si no respeta el shape. */
  definition: Record<string, unknown>
  /** Metodología del tenant (OT-0053). Omitida = genérica; obligatoria
   * para un ciclo de vida. Con metodología, la clave lleva su código:
   * `<prefijo>-<código>-<nombre>` (422 `TEMPLATE_KEY_METHODOLOGY_PREFIX`). */
  methodology_id?: string | undefined
}

// ─── ArtifactDefinition (TS mirror del Pydantic schema, B.6.d.0) ─────────────

/** Block types del catálogo Doc 06 §2. Espejo de `BlockType` enum del
 * backend (`app/schemas/template_definition.py`). El backend acepta
 * todos los listados aquí; el Designer expone solo el subset marcado
 * como `available: true` en `blockTypeCatalog.ts`. */
export type BlockType =
  // Texto
  | 'rich_text'
  | 'plain_text'
  | 'key_value_grid'
  // Listas
  | 'bullet_list'
  | 'numbered_list'
  | 'checklist'
  // Tabla
  | 'structured_table'
  // Diagramas Mermaid
  | 'diagram'
  | 'mermaid_flowchart'
  | 'mermaid_sequence'
  | 'mermaid_use_case'
  | 'mermaid_activity'
  | 'mermaid_state'
  | 'mermaid_er'
  | 'mermaid_class'
  | 'mermaid_gantt'
  | 'mermaid_journey'
  | 'mermaid_mindmap'
  | 'mermaid_generic'
  // Otros
  | 'code_block'
  | 'structured'
  // Contenido fijo
  | 'fixed_heading'
  | 'fixed_block'
  // Pendientes (B.6.d.3.c — necesitan B.7)
  | 'image_upload'
  | 'file_upload'
  | 'embed_canon'

/** Regla de validación aplicada a una sección. El catálogo `rule` es
 * abierto — los conocidos hoy (Doc 24 §1.5): min_length, max_length,
 * min_items, max_items, min_item_length, max_item_length, min_rows,
 * max_rows, regex, all_required_sections_filled. */
export interface ValidationRule {
  rule: string
  value?: unknown
}

// ─── Doc 24 v2 — AI instructions detalladas + table config ────────────────

/** Ejemplo bad de output con explicación de por qué es malo. Va dentro
 * de `ExamplesBlock.bad`. (Doc 24 §2.5) */
export interface BadExample {
  content: string
  explanation: string
}

/** Pares de ejemplos (bueno + malo) que el agente recibe como few-shot. */
export interface ExamplesBlock {
  good?: string | null
  bad?: BadExample | null
}

/** Instrucciones detalladas para que la IA llene una sección — shape v2.
 * Reemplaza el `Record<string, string>` plano de v1 con un objeto rico.
 * (Doc 24 §2.5 — modelo Pydantic `DetailedAiInstructions`) */
export interface DetailedAiInstructions {
  purpose?: string | null
  must_include?: string[]
  must_avoid?: string[]
  examples?: ExamplesBlock | null
}

/** Lado localizable de una columna de `structured_table` — vive bajo
 * `column.i18n.{lang}`. (Doc 24 §2.6) */
export interface TableColumnI18n {
  label: string
  placeholder?: string | null
}

/** Lado localizable de un valor enum dentro de una columna. */
export interface EnumValueI18n {
  label: string
}

/** Valor de enum usable en una columna `type=enum` de un structured_table.
 * La `value` es estable (snake_case); `i18n.{lang}.label` es lo que el
 * usuario ve en el dropdown. */
export interface EnumValue {
  value: string
  i18n: Record<string, EnumValueI18n>
}

/** Definición v2 de una columna de structured_table. Sustituye al
 * legacy `StructuredTableColumn` (Doc 24 §2.6).
 *
 * **Tipo `currency` (Doc 19 §10):** una celda guarda `{amount, code}` y
 * se renderea con `Intl.NumberFormat(locale, {style: 'currency', currency: code})`.
 * `default_currency` pre-selecciona el dropdown al crear filas;
 * `allowed_currencies` restringe el dropdown. Ambos solo válidos en
 * `type === 'currency'` — backend rechaza data leak en otros tipos.
 *
 * **Tipo `autonum` (Doc 06 §Backlog futuro):** IDs auto-numéricos con
 * prefijo (RSK-001, ISS-042). El cell value NO se persiste — el render
 * lo calcula desde `rowIndex + 1`, `prefix`, `pad`. Política de borrado:
 * gap permitido (no renumerar), igual que Jira/Linear/GitHub Issues.
 * Una columna autonum NO puede ser `required` ni tener `validation`.
 */
export interface TableColumn {
  /** Key estable, snake_case. Se usa como ID interno por celda. */
  key: string
  type: 'plain_text' | 'enum' | 'number' | 'date' | 'currency' | 'autonum'
  required?: boolean
  validation?: ValidationRule[]
  /** Solo cuando `type === 'enum'`. */
  enum_values?: EnumValue[] | null
  /** Solo cuando `type === 'currency'`. ISO 4217 (3 letras MAYÚSCULAS).
   * Default que pre-selecciona el dropdown al crear nueva fila. */
  default_currency?: string | null
  /** Solo cuando `type === 'currency'`. Lista opcional de códigos ISO
   * 4217 que restringen el dropdown del editor. Si es null/ausente,
   * cualquier código ISO 4217 conocido se permite. */
  allowed_currencies?: string[] | null
  /** Solo cuando `type === 'autonum'`. Prefijo del ID (ej. 'RSK-').
   * Puede ser '' (string vacío) para solo ordinales sin prefijo. */
  prefix?: string | null
  /** Solo cuando `type === 'autonum'`. Padding del ordinal (0-10).
   * `pad=3` produce 001, 010, 100; `pad=0` desactiva padding. */
  pad?: number | null
  i18n: Record<string, TableColumnI18n>
}

/** Doc 19 §10 — Shape del value de una celda `type='currency'` en el
 * content del artifact. Cada fila tiene su propia moneda (per-cell).
 * Si `code` es null/ausente, el render aplica `column.default_currency`. */
export interface CurrencyCellValue {
  amount: number
  code?: string | null
}

/** Config completa de un structured_table. Va en
 * `ArtifactStructuralSection.table_config` cuando `type === 'structured_table'`.
 * (Doc 24 §2.6) */
export interface TableConfig {
  columns: TableColumn[]
}

/** Metadata estructural — fuera del bloque i18n.
 *
 * **Recursivo (B.6.d.3.b'):** `children[]` permite anidar nodos. Solo
 * los tipos contenedor (hoy: `fixed_heading`) pueden tener children.
 * `repeatable: true` + container = grupo instanciable N veces en runtime.
 */
export interface ArtifactStructuralSection {
  /** ID estable y único en TODO el árbol, snake_case. NO se traduce. */
  section_id: string
  type: string
  order: number
  required?: boolean
  repeatable?: boolean
  /** Mínimo de instancias requerido cuando `repeatable: true`. */
  min_items?: number
  /** Máximo de instancias permitido cuando `repeatable: true`. */
  max_items?: number
  /** Hijos del nodo (solo permitido en tipos contenedor como
   * `fixed_heading`). Default: ausente o array vacío para hojas. */
  children?: ArtifactStructuralSection[]
  validation?: ValidationRule[]
  /** Sub-fase 2.1++: section_ids del MISMO template cuyo contenido se
   * inyecta automáticamente como contexto al prompt de la IA cuando
   * esta sección se genera. El template author lo declara en el
   * Designer; el resolver del backend lo lee y lo materializa contra
   * el `current_version.content` del artifact. Vacío o ausente = sin
   * contexto interno. */
  context_sections?: string[]
  /** Doc 24 §2.3 v2: config formal de structured_table (columns con i18n +
   * enum_values + validation por columna). null/ausente para cualquier otro
   * `type`. Reemplaza al legacy `columns: StructuredTableColumn[]` que
   * vivía como campo dinámico (`[extra: string]: unknown`). */
  table_config?: TableConfig | null
  // Ajustes que dependen del tipo de bloque (OT-0006). El backend rechaza
  // cada uno fuera de sus tipos (`_SETTING_BLOCK_TYPES`).
  /** `key_value_grid`: claves fijas; el usuario solo llena los valores. */
  predefined_keys?: string[]
  /** `code_block`: lenguaje inicial. */
  default_language?: string
  /** `fixed_block`: tono del callout. */
  variant?: 'note' | 'info' | 'tip' | 'warning' | 'critical' | 'success' | 'reference' | 'instruction'
}

/** Lado localizable de una sección (vive en `i18n.{lang}.sections[]`). */
export interface ArtifactI18nSection {
  section_id: string
  title: string
  /** Doc 24 v2 — una línea descriptiva visible en sidebar del editor. */
  description?: string
  /** Texto fijo del callout (`fixed_block`). Para otros tipos no se usa. */
  body?: string
  /** Texto que aparece en gris dentro del input cuando está vacío. Aplica
   * a tipos hoja editables por el usuario. */
  placeholder?: string
  /** Texto pequeño debajo del input explicando al usuario qué se espera.
   * Aplica a tipos hoja editables por el usuario. */
  help_text?: string
  /** Doc 24 v2: shape rico `DetailedAiInstructions` con purpose +
   * must_include + must_avoid + examples (good + bad). El union acepta
   * también el shape v1 legacy (`Record<string, string>` plano) para
   * que componentes designer/viewer migren gradualmente en Fases 6+. */
  ai_instructions?: DetailedAiInstructions | Record<string, string>
  /** @deprecated Doc 24 v2: los labels de columna ahora viven en
   * `TableColumn.i18n.{lang}.label` (parte de `TableConfig`). Este campo
   * queda solo para retrocompatibilidad con snapshots v1. */
  column_labels?: Record<string, string>
}

/** Una columna del schema de un `structured_table`. Vive en el
 * structural section como `columns: StructuredTableColumn[]`. */
export interface StructuredTableColumn {
  /** Key estable, slug-like. NO se traduce. Usada para hash las cells
   * en cada row. */
  key: string
  /** Tipo de la columna — determina el input en runtime y el render
   * de la celda. */
  type:
    | 'text'
    | 'number'
    | 'date'
    | 'enum'
    | 'select'
    | 'autonum'
  /** Si la celda es obligatoria al llenar la fila. */
  required?: boolean
  /** Para `enum` y `select`: lista de opciones permitidas. */
  options?: string[]
  /** Para `autonum`: prefix textual (ej. "UC-"). */
  prefix?: string
  /** Para `autonum`: cantidad de dígitos a paddear (ej. 3 → "001"). */
  pad?: number
}

/** Bloque i18n por idioma (`i18n.es` / `i18n.en` / ...).
 *
 * Además de name/description, lleva las instrucciones de IA al nivel de
 * documento que aplican a TODAS las secciones (Doc 06 §1.2):
 *
 *   - `tone_audience`: tono y audiencia del documento — antes vivía
 *     redundantemente como `ai_instructions.tone` en cada sección.
 *   - `ai_global_instructions`: instrucciones generales de IA para todo
 *     el documento (qué es, para qué, criterios transversales). Cada
 *     sección puede sumar las suyas vía `ai_instructions.purpose`.
 */
export interface ArtifactI18nBlock {
  name: string
  description?: string
  /** Doc 24 v2 — para qué sirve el artefacto, qué problema resuelve.
   * Lo consume la IA y se muestra como tip al autor en el editor.
   * `null` cuando el author limpia el campo (compat backend Pydantic
   * que acepta `None`). */
  purpose?: string | null
  tone_audience?: string | null
  /** Doc 24 v2 — lista de reglas IA aplicables a TODAS las secciones del
   * documento. El union acepta `string` para retrocompatibilidad con
   * snapshots v1 que tenían una sola regla concatenada. */
  ai_global_instructions?: string[] | string
  sections: ArtifactI18nSection[]
}

/** Shape canónico de Artifact Template v2 (Doc 06 §1.1 + Doc 24).
 *
 * Eliminado en v2 (Fase 1 backend + Fase 11 frontend): `ai_document_config`
 * (con sus sub-shapes `AgentToolsConfig`, `SearchRestrictions`,
 * `vocabulary_refs`, `hints`). El backend Pydantic ahora rechaza esos
 * campos con `extra="forbid"`. La configuración del agente IA a nivel doc
 * vive en `i18n.{lang}.{purpose, tone_audience, ai_global_instructions}`. */
export interface ArtifactDefinition {
  template_id?: string
  template_type: 'artifact'
  template_version: string
  phase_id?: string
  subphase_id?: string
  is_optional?: boolean
  is_system_default?: boolean
  tags?: string[]
  i18n: Partial<Record<SupportedLocale, ArtifactI18nBlock>>
  sections: ArtifactStructuralSection[]
  validations_global?: ValidationRule[]
  [extra: string]: unknown
}

export async function createTemplate(
  accessToken: string,
  body: CreateTemplateBody,
): Promise<Template> {
  return apiFetch<Template>('/api/v1/templates', {
    accessToken,
    method: 'POST',
    body,
  })
}

// ─── LifecycleDefinition (TS mirror del Pydantic schema, B.6.d.9.a) ──────────

/** Modo de contrato de una phase del lifecycle: ¿se contrata o no?
 * (OT-0007 §1/§4.1). Binario — quién PRODUCE cada documento
 * (plantilla vs. archivo aportado) es una decisión independiente, por
 * artifact, que se toma en Lifecycle en runtime
 * (`ExpectedArtifact.default_source_mode` / `Artifact.source_mode`),
 * no al diseñar el template. `'external_canon'` ya no es un valor
 * válido — el backend lo sigue tolerando en datos legacy (proyectos
 * creados antes de esta OT) pero lo rechaza en escritura nueva. */
export type LifecycleContractMode = 'internal' | 'skipped'

/** Artifact que el lifecycle declara que cada proyecto debe materializar
 * al crearse en una phase específica. `subphase_code` es referencia
 * libre cuando la phase no declara `subphases[]`, o debe matchear uno
 * de los códigos cuando sí los declara.
 *
 * `template_key`: identifica QUÉ entregable se espera, independiente de
 * cómo se produce (`default_source_mode`, OT-0007 §4.1). **Siempre
 * requerido, en los dos modos**: incluso cuando el contenido lo aporta
 * el cliente hace falta saber qué entregable se espera para poder
 * materializar el `Artifact` (nombre, template_definition_id) al crear
 * el proyecto. Lo que trae el cliente es el *contenido*, no la identidad
 * del entregable. El backend lo enforce con `template_key: str`
 * (`app/schemas/template_definition.py`).
 *   - phase 'skipped' → no admite expected_artifacts.
 *
 * `user_can_add_more`: cuando true, el equipo del proyecto puede
 * crear instancias adicionales en runtime (ej. casos de prueba CP 1,
 * CP 2, ... CP N). (B.6.d.9.i)
 *
 * `role_template_key`: referencia opcional a un Role Template del
 * catálogo. (B.6.d.9.j)
 */
export interface ExpectedArtifact {
  template_key: string
  subphase_code: string
  default_count?: number
  is_optional?: boolean
  /** OT-0007 §4.1 — default 'template' en el backend cuando se omite. */
  default_source_mode?: 'template' | 'upload'
  user_can_add_more?: boolean
  role_template_key?: string | null
}

/** Sub-fase declarada inline dentro de una phase. Optional —
 * la mayoría de phases no las usan. Auto-numeración recomendada:
 * `<phase_code>.<n>` (ej. `discovery.1`). */
export interface LifecycleSubphase {
  subphase_code: string
  subphase_order: number
  [extra: string]: unknown
}

/** Phase declarada por el lifecycle template.
 *
 * - `gate_description`: legacy structural (seed HSL). El Designer
 *   nuevo escribe el texto user-facing en `i18n.{lang}.phases[]`.
 * - `subphases`: vacío por default; declaras subphases solo cuando una
 *   phase tiene granularidad propia (ej. Design.1 Architecture +
 *   Design.2 UX).
 * - `contract_mode_overridable`: cuando `false` (default), el wizard
 *   de creación de proyecto bloquea el dropdown de modo en el default.
 *   Cuando `true`, el default pre-selecciona pero el usuario puede
 *   cambiarlo.
 */
export interface LifecyclePhase {
  phase_code: string
  phase_order: number
  default_contract_mode: LifecycleContractMode
  contract_mode_overridable?: boolean
  /** @deprecated — usar `i18n.{lang}.phases[].gate_description`. Aquí
   * se queda por backward-compat con el seed `tpl-lifecycle-hsl`. */
  gate_description?: string
  subphases?: LifecycleSubphase[]
  expected_artifacts?: ExpectedArtifact[]
  [extra: string]: unknown
}

/** Espejo localizable de una sub-fase. */
export interface LifecycleSubphaseI18n {
  subphase_code: string
  name?: string
  description?: string
}

/** Espejo localizable de una phase. Lleva los textos visibles que en
 * el modelo legacy vivían en el structural. */
export interface LifecyclePhaseI18n {
  phase_code: string
  name?: string
  description?: string
  gate_description?: string
  subphases?: LifecycleSubphaseI18n[]
}

/** Bloque i18n del lifecycle (per locale). */
export interface LifecycleI18nBlock {
  name: string
  description?: string
  /** Espejo i18n de las phases del structural. Vacío permitido por
   * backward-compat (seed legacy sin este bloque). */
  phases?: LifecyclePhaseI18n[]
}

/** Shape canónico del Lifecycle Template (Doc 06 §5). El frontend lo
 * carga, edita en el Lifecycle Designer (B.6.d.9), y lo persiste vía
 * `updateVersionDefinition`. */
export interface LifecycleDefinition {
  template_id?: string
  template_type: 'lifecycle'
  template_version: string
  is_system_default?: boolean
  i18n: Partial<Record<SupportedLocale, LifecycleI18nBlock>>
  phases: LifecyclePhase[]
  [extra: string]: unknown
}

/** Default de una phase nueva al añadirla en el Designer. */
export const DEFAULT_LIFECYCLE_PHASE_PARTIAL: Pick<
  LifecyclePhase,
  'default_contract_mode' | 'contract_mode_overridable' | 'subphases' | 'expected_artifacts'
> = {
  default_contract_mode: 'internal',
  contract_mode_overridable: false,
  subphases: [],
  expected_artifacts: [],
}

// ─── RoleDefinition (TS mirror del Pydantic schema, B.6.d.9.j) ──────────────

/** Bloque i18n de un Role Template (per locale). Mucho más simple que
 * el de un artifact: solo nombre + descripción + lista de
 * responsabilidades. */
export interface RoleI18nBlock {
  name: string
  description?: string
  responsibilities?: string[]
}

/** Shape canónico de un Role Template. Referenciado desde
 * `ExpectedArtifact.role_template_key` para declarar quién es
 * responsable de cada entregable del lifecycle. */
export interface RoleDefinition {
  template_id?: string
  template_type: 'role'
  template_version: string
  is_system_default?: boolean
  i18n: Partial<Record<SupportedLocale, RoleI18nBlock>>
  /** A qué lado de la mesa aplica. `client_stakeholder` convierte en firma
   * del cliente la etapa que pide el rol. Omitido = interno (roles
   * anteriores al campo). */
  applicable_scopes?: RoleScope[]
  [extra: string]: unknown
}

export type RoleScope = 'internal' | 'client_stakeholder'


// ─── ApprovalWorkflowDefinition (mirror del Pydantic schema, Doc 21) ──────

/** Nombre i18n de un stage del workflow. */
/** Nombre del stage por idioma; al menos uno (OT-0055: antes es y en
 * obligatorios). Quien lo muestra cae a otro idioma con `pickLocalized`. */
export type WorkflowStageI18n = Partial<Record<SupportedLocale, string>>

/** Bloque i18n a nivel template (header del catálogo). */
export interface WorkflowHeaderI18n {
  name: string
  description?: string
}

/** Un stage del workflow declarativo. */
export interface WorkflowStage {
  order: number
  name: WorkflowStageI18n
  mode: 'parallel'
  required_roles: string[]
  min_approvals: 'all' | number
  exclude_self?: boolean
  allow_skip_if_no_role_assignee?: boolean
}

/** Shape canónico de un Approval Workflow Template. Referenciado desde
 * `lifecycle_subphase.approval_workflow_template_key` para declarar
 * el flujo de aprobaciones de los artifacts de esa subphase. */
export interface ApprovalWorkflowDefinition {
  template_id?: string
  template_type: 'approval_workflow'
  template_version: string
  is_system_default?: boolean
  i18n: Partial<Record<SupportedLocale, WorkflowHeaderI18n>>
  stages: WorkflowStage[]
  allow_user_override: boolean
  [extra: string]: unknown
}
